// WAAP AI-WAF → Custom Rules (user-defined detection rules).
//
// Own portal surface (custom_strategy / custom_strategies — no `type`, no
// strategy_type in relations), backed by eagle_waf/advanced:
//
//   GET    /waap/domains/:id/custom-rules          → portal GET    /custom_strategies
//   POST   /waap/domains/:id/custom-rules          → portal POST   /custom_strategy
//   GET    /waap/domains/:id/custom-rules/:ruleId  → portal GET    /custom_strategy/:id
//   PUT    /waap/domains/:id/custom-rules/:ruleId  → portal PUT    /custom_strategy (id in body)
//   DELETE /waap/domains/:id/custom-rules/:ruleId  → portal DELETE /custom_strategy/:id
//
// The FE owns the per-rule `config`; the BE injects name / sqlinjection_comment /
// sites. List/detail reuse the advanced list-item / strategy shapes.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import type { AdvancedStrategy, AdvancedStrategyListItem } from './advanced-strategy';

/** A custom-rule create/update body (no `type`). */
export interface CustomRulePayload<TConfig = Record<string, unknown>> {
  name: string;
  description: string;
  sites: number[];
  config: TConfig;
}

function basePath(domainId: string): string {
  return `/waap/domains/${domainId}/custom-rules`;
}

export const customRuleKeys = {
  all: ['waap', 'custom-rules'] as const,
  list: (domainId: string) => ['waap', 'custom-rules', domainId] as const,
  one: (domainId: string, id: string | number) =>
    ['waap', 'custom-rules', domainId, String(id)] as const,
};

/** List the account's custom rules. */
export function useCustomRules(domainId: string | undefined) {
  return useQuery({
    queryKey: customRuleKeys.list(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<AdvancedStrategyListItem[]> => {
      const res = await serviceApiV3.get<
        AdvancedStrategyListItem[] | { data: AdvancedStrategyListItem[] }
      >(basePath(domainId as string));
      const body = res.data as AdvancedStrategyListItem[] | { data?: AdvancedStrategyListItem[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Fetch one custom rule (for the edit form). */
export function useCustomRule<TConfig = Record<string, unknown>>(
  domainId: string | undefined,
  id: string | undefined,
) {
  return useQuery({
    queryKey: customRuleKeys.one(domainId ?? '', id ?? ''),
    enabled: !!domainId && !!id,
    retry: false,
    queryFn: async (): Promise<AdvancedStrategy<TConfig>> => {
      const res = await serviceApiV3.get<
        AdvancedStrategy<TConfig> | { data: AdvancedStrategy<TConfig> }
      >(`${basePath(domainId as string)}/${id}`);
      const body = res.data as AdvancedStrategy<TConfig> | { data?: AdvancedStrategy<TConfig> };
      return (
        (body as { data?: AdvancedStrategy<TConfig> })?.data ?? (body as AdvancedStrategy<TConfig>)
      );
    },
  });
}

/** Create a custom rule. */
export function useCreateCustomRule<TConfig = Record<string, unknown>>(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, CustomRulePayload<TConfig>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CustomRulePayload<TConfig>) => {
      const res = await serviceApiV3.post(basePath(domainId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: customRuleKeys.list(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a custom rule. */
export function useUpdateCustomRule<TConfig = Record<string, unknown>>(
  domainId: string,
  id: number | string,
  options?: UseMutationOptions<unknown, unknown, CustomRulePayload<TConfig>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CustomRulePayload<TConfig>) => {
      const res = await serviceApiV3.put(`${basePath(domainId)}/${id}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: customRuleKeys.list(domainId) });
      qc.invalidateQueries({ queryKey: customRuleKeys.one(domainId, id) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a custom rule. */
export function useDeleteCustomRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await serviceApiV3.delete(`${basePath(domainId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: customRuleKeys.list(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Service-scoped twins (account-level, work with zero websites) ----------

function serviceBasePath(serviceId: string): string {
  return `/services/${serviceId}/waap/custom-rules`;
}

export const customRuleServiceKeys = {
  list: (serviceId: string) => ['waap', 'custom-rules', 'service', serviceId] as const,
  one: (serviceId: string, id: string | number) =>
    ['waap', 'custom-rules', 'service', serviceId, String(id)] as const,
};

/** List custom rules, resolved from the service. */
export function useServiceCustomRules(serviceId: string | undefined) {
  return useQuery({
    queryKey: customRuleServiceKeys.list(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<AdvancedStrategyListItem[]> => {
      const res = await serviceApiV3.get<
        AdvancedStrategyListItem[] | { data: AdvancedStrategyListItem[] }
      >(serviceBasePath(serviceId as string));
      const body = res.data as AdvancedStrategyListItem[] | { data?: AdvancedStrategyListItem[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Fetch one custom rule, resolved from the service. */
export function useServiceCustomRule<TConfig = Record<string, unknown>>(
  serviceId: string | undefined,
  id: string | undefined,
) {
  return useQuery({
    queryKey: customRuleServiceKeys.one(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async (): Promise<AdvancedStrategy<TConfig>> => {
      const res = await serviceApiV3.get<
        AdvancedStrategy<TConfig> | { data: AdvancedStrategy<TConfig> }
      >(`${serviceBasePath(serviceId as string)}/${id}`);
      const body = res.data as AdvancedStrategy<TConfig> | { data?: AdvancedStrategy<TConfig> };
      return (
        (body as { data?: AdvancedStrategy<TConfig> })?.data ?? (body as AdvancedStrategy<TConfig>)
      );
    },
  });
}

/** Create a custom rule, service-scoped. */
export function useCreateServiceCustomRule<TConfig = Record<string, unknown>>(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, CustomRulePayload<TConfig>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CustomRulePayload<TConfig>) => {
      const res = await serviceApiV3.post(serviceBasePath(serviceId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: customRuleServiceKeys.list(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a custom rule, service-scoped. */
export function useUpdateServiceCustomRule<TConfig = Record<string, unknown>>(
  serviceId: string,
  id: number | string,
  options?: UseMutationOptions<unknown, unknown, CustomRulePayload<TConfig>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CustomRulePayload<TConfig>) => {
      const res = await serviceApiV3.put(`${serviceBasePath(serviceId)}/${id}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: customRuleServiceKeys.list(serviceId) });
      qc.invalidateQueries({ queryKey: customRuleServiceKeys.one(serviceId, id) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a custom rule, service-scoped. */
export function useDeleteServiceCustomRule(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await serviceApiV3.delete(`${serviceBasePath(serviceId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: customRuleServiceKeys.list(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}
